import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";

export interface AnnouncementState {
  id: string;
  name: string;
  description: string;
  interval: null | {
    interval: any;
    crontab: any;
    solar: any;
  };
  starting_at: string;
  schedule_type: [string, string];
  scheduled_for: string;
  last_updated: string;
  text: { [key: string]: string };
  sent_by: string;
  rooms: string;
  type: [string, string];
  last_run_at: null | string;
  status: [string, string];
  total_run_count: number;
}

export interface ContactBookState {
  id: string;
  created_by: string;
  last_updated_by: string | null;
  name: string;
  address_book_id: string;
  contacts: any[];
  rooms_applied_to: string[];
}

export interface PaginationProps {
  has_previous: boolean;
  has_next: boolean;
  current_page: number;
  total_pages: number;
  total_objects: number;
}

// Define a type for the slice state
export interface CommunicationState {
  announcements: {
    data: AnnouncementState[] | null;
    pagination: PaginationProps;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  contact_books: {
    data: ContactBookState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  options: {
    [key: string]: any;
  };
}

// Define the initial state using that type
const initialState: CommunicationState = {
  announcements: {
    data: null,
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0,
    },
    loading: false,
    waiting: false,
    errors: [],
  },
  contact_books: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  options: {},
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get announcements
export const getAnnouncements = createAsyncThunk(
  "communication/announcements/get",
  async (
    obj: {
      pageNum: number | boolean;
      type: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/announcement/?page=${obj.pageNum}`;
      if (obj.type) url += `&type=${obj.type}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create announcement now
export const createAnnouncementNow = createAsyncThunk(
  "communication/announcements/now/post",
  async (
    obj: {
      text: { [key: string]: string };
      rooms: string[];
      type: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = { text: obj.text, rooms: obj.rooms, type: obj.type };

      // Build URL
      const url = "/api/announcement/now/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create announcement that is scheduled
export const createAnnouncementScheduled = createAsyncThunk(
  "communication/announcements/scheduled/post",
  async (
    obj: {
      text: { [key: string]: string };
      rooms: string[];
      datetime: string;
      type: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        text: obj.text,
        rooms: obj.rooms,
        datetime: obj.datetime,
        type: obj.type,
      };

      // Build URL
      const url = "/api/announcement/scheduled/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create announcement on a recurring interval
export const createAnnouncementRecurringInterval = createAsyncThunk(
  "communication/announcements/recurring/interval/post",
  async (
    obj: {
      text: { [key: string]: string };
      rooms: string[];
      type: string;
      start_time: string;
      schedule_type: string;
      schedule_params: {
        every: number;
        period: string;
      };
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        text: obj.text,
        rooms: obj.rooms,
        type: obj.type,
        start_time: obj.start_time,
        schedule_type: obj.schedule_type,
        schedule_params: obj.schedule_params,
      };

      // Build URL
      const url = "/api/announcement/periodic/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create announcement on a recurring period
export const createAnnouncementRecurringPeriod = createAsyncThunk(
  "communication/announcements/recurring/period/post",
  async (
    obj: {
      text: { [key: string]: string };
      rooms: string[];
      type: string;
      schedule_type: string;
      schedule_params: {
        [key: string]: any;
      };
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        text: obj.text,
        rooms: obj.rooms,
        type: obj.type,
        schedule_type: obj.schedule_type,
        schedule_params: obj.schedule_params,
      };

      // Build URL
      const url = "/api/announcement/periodic/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete announcements
export const deleteAnnouncements = createAsyncThunk(
  "communication/announcements/delete",
  async (
    obj: {
      announcementIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.announcementIds },
      };

      const res = await axios.delete("/api/announcement/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update status of periodic announcements
export const updatePeriodicAnnouncementStatus = createAsyncThunk(
  "communication/announcements/periodic/status/put",
  async (
    obj: {
      announcementIds: string[];
      status: "E" | "D";
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = { id: obj.announcementIds, status: obj.status };
      const res = await axios.put("/api/announcement/periodic/", body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get contact books
export const getContactBooks = createAsyncThunk(
  "communication/contact-books/get",
  async (args, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/communication/address-book/`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create contact book
export const createContactBook = createAsyncThunk(
  "communication/contact-books/post",
  async (
    obj: {
      name: string;
      property: string;
      contacts: any[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        property: obj.property,
        contacts: obj.contacts,
      };

      // Build URL
      const url = "/api/communication/address-book/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update contact book
export const updateContactBook = createAsyncThunk(
  "communication/contact-books/put",
  async (
    obj: {
      contactBookId: string;
      name: string;
      property: string;
      contacts: any[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.contactBookId,
        name: obj.name,
        property: obj.property,
        contacts: obj.contacts,
      };

      // Build URL
      const url = "/api/communication/address-book/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete contact book
export const deleteContactBooks = createAsyncThunk(
  "communication/contact-books/delete",
  async (
    obj: {
      contactBookIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.contactBookIds },
      };

      const res = await axios.delete(
        "/api/communication/address-book/",
        config,
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Apply contact book to rooms
export const applyContactBookToRooms = createAsyncThunk(
  "communication/contact-books/apply/room/put",
  async (
    obj: {
      contactBookId: string;
      property: string;
      rooms: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        rooms: obj.rooms,
        property: obj.property,
        address_book: obj.contactBookId,
      };

      // Build URL
      const url = "/api/communication/address-book/associate/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Save reciprocal associations for room
export const saveReciprocalAssociations = createAsyncThunk(
  "communication/contact-books/reciprocation/put",
  async (
    obj: {
      profileId: string;
      property: string;
      contacts: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        profile_id: obj.profileId,
        property: obj.property,
        contact_id: obj.contacts,
      };

      // Build URL
      const url = "/api/communication/contact/reciprocate/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const communicationSlice = createSlice({
  name: "communication",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get announcements
      .addCase(getAnnouncements.pending, (state) => {
        state.announcements.loading = true;
        state.announcements.waiting = false;
      })
      .addCase(getAnnouncements.fulfilled, (state, action) => {
        state.announcements.data = action.payload.data.data;
        state.announcements.pagination = action.payload.data.pagination;
        state.options = action.payload.data.options;
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })
      .addCase(getAnnouncements.rejected, (state) => {
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })

      // Create announcement now
      .addCase(createAnnouncementNow.pending, (state) => {
        state.announcements.loading = true;
        state.announcements.waiting = false;
      })
      .addCase(createAnnouncementNow.fulfilled, (state, action) => {
        state.announcements.data =
          state.announcements.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.announcements.data];
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })
      .addCase(createAnnouncementNow.rejected, (state) => {
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })

      // Create announcement that is scheduled
      .addCase(createAnnouncementScheduled.pending, (state) => {
        state.announcements.loading = true;
        state.announcements.waiting = false;
      })
      .addCase(createAnnouncementScheduled.fulfilled, (state, action) => {
        state.announcements.data =
          state.announcements.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.announcements.data];
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })
      .addCase(createAnnouncementScheduled.rejected, (state) => {
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })

      // Create announcement on a recurring interval
      .addCase(createAnnouncementRecurringInterval.pending, (state) => {
        state.announcements.loading = true;
        state.announcements.waiting = false;
      })
      .addCase(
        createAnnouncementRecurringInterval.fulfilled,
        (state, action) => {
          state.announcements.data =
            state.announcements.data === null
              ? [action.payload.data]
              : [action.payload.data, ...state.announcements.data];
          state.announcements.loading = false;
          state.announcements.waiting = false;
        },
      )
      .addCase(createAnnouncementRecurringInterval.rejected, (state) => {
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })

      // Create announcement on a recurring period
      .addCase(createAnnouncementRecurringPeriod.pending, (state) => {
        state.announcements.loading = true;
        state.announcements.waiting = false;
      })
      .addCase(createAnnouncementRecurringPeriod.fulfilled, (state, action) => {
        state.announcements.data =
          state.announcements.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.announcements.data];
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })
      .addCase(createAnnouncementRecurringPeriod.rejected, (state) => {
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })

      // Delete announcements
      .addCase(deleteAnnouncements.pending, (state) => {
        state.announcements.loading = true;
        state.announcements.waiting = false;
      })
      .addCase(deleteAnnouncements.fulfilled, (state, action) => {
        const existingData =
          state.announcements.data === null ? [] : state.announcements.data;
        const updatedData = existingData.filter(
          (announcement) => !action.payload.data.id.includes(announcement.id),
        );
        state.announcements.data = updatedData;
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })
      .addCase(deleteAnnouncements.rejected, (state) => {
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })

      // Update status of periodic announcements
      .addCase(updatePeriodicAnnouncementStatus.pending, (state) => {
        state.announcements.loading = true;
        state.announcements.waiting = false;
      })
      .addCase(updatePeriodicAnnouncementStatus.fulfilled, (state, action) => {
        const updatedIds = action.payload.data.map((x: any) => x.id);
        const existingData =
          state.announcements.data === null ? [] : state.announcements.data;
        const updatedData = [];
        for (let i = 0; i < existingData.length; i++) {
          if (updatedIds.includes(existingData[i].id)) {
            updatedData.push(
              action.payload.data[updatedIds.indexOf(existingData[i].id)],
            );
          } else {
            updatedData.push(existingData[i]);
          }
        }
        state.announcements.data = updatedData;
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })
      .addCase(updatePeriodicAnnouncementStatus.rejected, (state) => {
        state.announcements.loading = false;
        state.announcements.waiting = false;
      })

      // Get contact books
      .addCase(getContactBooks.pending, (state) => {
        state.contact_books.loading = true;
        state.contact_books.waiting = false;
      })
      .addCase(getContactBooks.fulfilled, (state, action) => {
        state.contact_books.data = action.payload.data.data;
        state.options.contact_type = action.payload.data.options.contact_type;
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      })
      .addCase(getContactBooks.rejected, (state) => {
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      })

      // Create contact book
      .addCase(createContactBook.pending, (state) => {
        state.contact_books.loading = true;
        state.contact_books.waiting = false;
      })
      .addCase(createContactBook.fulfilled, (state, action) => {
        state.contact_books.data =
          state.contact_books.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.contact_books.data];
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      })
      .addCase(createContactBook.rejected, (state) => {
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      })

      // Update contact book
      .addCase(updateContactBook.pending, (state) => {
        state.contact_books.loading = true;
        state.contact_books.waiting = false;
      })
      .addCase(updateContactBook.fulfilled, (state, action) => {
        const updatedId = action.payload.data.id;
        const existingData =
          state.contact_books.data === null ? [] : state.contact_books.data;
        const updatedData = [];
        for (let i = 0; i < existingData.length; i++) {
          if (existingData[i].id === updatedId) {
            updatedData.push(
              action.payload.data[updatedId.indexOf(existingData[i].id)],
            );
          } else {
            updatedData.push(existingData[i]);
          }
        }
        state.contact_books.data = updatedData;
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      })
      .addCase(updateContactBook.rejected, (state) => {
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      })

      // Delete contact books
      .addCase(deleteContactBooks.pending, (state) => {
        state.contact_books.loading = true;
        state.contact_books.waiting = false;
      })
      .addCase(deleteContactBooks.fulfilled, (state, action) => {
        const existingData =
          state.contact_books.data === null ? [] : state.contact_books.data;
        const updatedData = existingData.filter(
          (book) => !action.payload.data.id.includes(book.id),
        );
        state.contact_books.data = updatedData;
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      })
      .addCase(deleteContactBooks.rejected, (state) => {
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      })

      // Apply contact book to rooms
      .addCase(applyContactBookToRooms.pending, (state) => {
        state.contact_books.loading = true;
        state.contact_books.waiting = false;
      })
      .addCase(applyContactBookToRooms.fulfilled, (state, action) => {
        const updatedId = action.payload.data.id;
        const existingData =
          state.contact_books.data === null ? [] : state.contact_books.data;
        const updatedData = [];
        for (let i = 0; i < existingData.length; i++) {
          if (existingData[i].id === updatedId) {
            updatedData.push(action.payload.data);
          } else {
            updatedData.push(existingData[i]);
          }
        }
        state.contact_books.data = updatedData;
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      })
      .addCase(applyContactBookToRooms.rejected, (state) => {
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      })

      // Save reciprocal associations for room
      .addCase(saveReciprocalAssociations.pending, (state) => {
        state.contact_books.loading = true;
        state.contact_books.waiting = false;
      })
      .addCase(saveReciprocalAssociations.fulfilled, (state, action) => {
        state.contact_books.data = action.payload.data;
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      })
      .addCase(saveReciprocalAssociations.rejected, (state) => {
        state.contact_books.loading = false;
        state.contact_books.waiting = false;
      });
  },
});

export default communicationSlice.reducer;
